import { BsArrowBarDown, BsArrowBarUp } from 'react-icons/bs';
import { FailedToGetData, H2, Hr, Loading, MapMarkerCurrentPosition } from 'src/components/_commons';
import { Fragment, useEffect, useState } from 'react';
import { generalBlue, googleMapConfig } from 'src/app-config';
import { generalHelpers, generalHthHelper, millisecondsFiveMinutes, sseEvents } from '@monorepo/helpers';
import { GoogleMap, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { houseToHouseServices } from 'src/services';
import { HTHAllBuildings, HTHAllDoNotCalls, hthMapStyle, HTHMarkerBuilding, HTHPolygonInfoBox } from 'src/components/_commons/house-to-house';
import { setAlertResponse } from 'src/store';
import { sse } from '@/app-config';
import { typeBlock, typeConfigDoc, typeHTHTerritory, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsMobile, useIdleTimeout } from 'src/custom-hooks';
import { useNavigate, useParams } from 'react-router';

export const HTHSearchPage = () => {
    const block = useParams().block as typeBlock | undefined ?? null;
    const congregation = parseInt(useParams().congregation ?? '0') as number;
    const territoryNumber = useParams().territoryNumber as typeTerritoryNumber | undefined;
    //
    const [congregationConfig, setCongregationConfig] = useState<typeConfigDoc | null>(null);
    const [hthTerritory, setHthTerritory] = useState<typeHTHTerritory | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [polygonId, setPolygonId] = useState(0);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [showMapSection, setShowMapSection] = useState(false);
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();
    const mapLoader = useJsApiLoader(googleMapConfig);
    const navigate = useNavigate();

    useIdleTimeout(millisecondsFiveMinutes, () => (() => navigate('/')));

    useEffect(() => {
        if (!congregation || isNaN(congregation) || !territoryNumber) {
            return;
        }
        houseToHouseServices.GetTerritory(territoryNumber, congregation, block).then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.config || !response.data.hthTerritory) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setHthTerritory(response.data.hthTerritory);
            setCongregationConfig(response.data.config);
        });
    }, [refreshCounter]);

    useEffect(() => {
        const handler = (e: MessageEvent) => {
            const data = JSON.parse(e.data);
            if (data.congregation !== congregation) {
                return;
            }
            if (!!data.territoryNumber && data.territoryNumber !== territoryNumber) {
                return;
            }
            setRefreshCounter(x => x + 1);
        };
        sse.addEventListener(sseEvents.hthChange, handler);
        return () => {
            sse.removeEventListener(sseEvents.hthChange, handler);
        };
    }, [congregation, territoryNumber]);

    if (isLoading) {
        return <Loading mt={'80px'} />;
    }

    if (!congregationConfig || !hthTerritory || !territoryNumber) {
        return <FailedToGetData />;
    }

    return (
        <>
        <H2 title={`TERRITORIO ${territoryNumber} ${!!block ? `Manzana ${generalHthHelper.MaskTheBlock(block, !!congregationConfig?.isUsingLettersForBlocks)}` : ''}`} />
        <Hr classes={'text-center my-5 mx-auto'} styles={{ maxWidth: isMobile ? '95%' : '85%' }} />
        <HTHAllDoNotCalls
            hthTerritory={hthTerritory}
        />
        <Hr classes={'text-center my-5 mx-auto'} styles={{ maxWidth: isMobile ? '95%' : '85%' }} />
        <div className={'container px-0'}>
            <h1 className={'container pointer btn-general-blue text-white py-3'}
                style={{
                    backgroundColor: generalBlue,
                    fontSize: isMobile ? '1.8rem' : '2.4rem',
                    fontWeight: 'bold',
                    margin: isMobile ? '30px auto 20px auto' : '60px auto 40px auto',
                    // maxWidth: '90%',
                    textAlign: 'center'
                }}
                onClick={() => setShowMapSection(x => !x)}
            >
                {showMapSection ?
                    <>OCULTAR MAPA <BsArrowBarUp size={isMobile ? '1.6rem' : '1.4rem'} /></>
                    :
                    <>VER MAPA <BsArrowBarDown size={isMobile ? '1.6rem' : '1.4rem'} /></>
                }
            </h1>
        </div>
        {showMapSection &&
            <>
            {mapLoader.isLoaded ?
                <div className={'position-relative'} >
                    <GoogleMap
                        id={googleMapConfig.id}
                        mapContainerClassName={'d-block m-auto'}
                        mapContainerStyle={{
                            height: isMobile ? '600px' : '500px',
                            width: isMobile ? '100%' : '90%'
                        }}
                        options={{
                            center: hthTerritory.centerCoords,
                            isFractionalZoomEnabled: true,
                            minZoom: 8,
                            panControl: true,
                            streetViewControl: true,
                            styles: hthMapStyle,
                            zoom: hthTerritory.zoom,
                            zoomControl: true,
                            zoomControlOptions: { position: google.maps.ControlPosition.LEFT_BOTTOM }
                        }}
                    >
                        {!!hthTerritory.faces?.length && hthTerritory.faces.map(polygon =>
                            <Fragment key={polygon.id}>
                                <Polygon
                                    onClick={() => setPolygonId(polygon.id)}
                                    options={{
                                        clickable: true,
                                        fillColor: generalBlue,
                                        fillOpacity: 0.92,
                                        strokeColor: '',
                                        strokeOpacity: 0.9,
                                        strokePosition: google.maps.StrokePosition.CENTER,
                                        strokeWeight: 10
                                    }}
                                    path={[
                                        polygon.coordsPoint1,
                                        polygon.coordsPoint2,
                                        polygon.coordsPoint3
                                    ]}
                                />
                                {polygonId === polygon.id &&
                                    <HTHPolygonInfoBox
                                        polygon={polygon}
                                        territoryNumber={territoryNumber}
                                        usingLettersForBlocks={!!congregationConfig.isUsingLettersForBlocks}
                                    />
                                }
                                {polygon.buildings
                                 .filter(b => b.coords && b.households.some(h => h.history.filter(x => !!x.state).map(x => x.timestamp).some(d => generalHelpers.IsToday(d))))
                                 .map((b, i) =>
                                    <HTHMarkerBuilding key={i}
                                        building={b}
                                    />
                                )}
                            </Fragment>
                        )}
                        <MapMarkerCurrentPosition
                            showError={false}
                        />
                    </GoogleMap>
                </div>
                :
                <Loading mt={'40px'} />
            }
            </>
        }
        <Hr classes={'text-center my-5 mx-auto'} styles={{ maxWidth: isMobile ? '95%' : '85%' }} />
        <HTHAllBuildings
            block={block}
            congregationConfig={congregationConfig}
            hthTerritory={hthTerritory}
        />
        </>
    );
}
